import React, { useContext, useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { API_BASE_URL } from '../../config/api';
import { useActiveProject } from '../../hooks/useActiveProject';
import { getProjectDisplayName } from '../../utils/projectName';
import { entityTone } from '../../utils/statusTone';
import "./Header.css"

/** Точка статуса проекта в селекторе: цвет = роль entityTone. */
const projectStatusDotClass = (status) => {
  if (!status) return 'header-project-dot';
  return `header-project-dot header-project-dot--${entityTone(status)}`;
};

const Header = ({ onToggleSidebar, sidebarOpen = false }) => {
  const { isAuthenticated, email, isAdmin, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProjectDropdownOpen, setIsProjectDropdownOpen] = useState(false);
  const menuRef = useRef(null);
  const projectDropdownRef = useRef(null);
  const {
    projects,
    projectsLoading,
    selectedProjectLabel,
    selectedProjectValue,
    selectedProjectStatus,
    isScopedRoute,
    projectIdFromPath,
    setActiveProject,
    clearActiveProject,
  } = useActiveProject();

  // Закрытие меню при клике вне его
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
      if (projectDropdownRef.current && !projectDropdownRef.current.contains(event.target)) {
        setIsProjectDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Закрытие меню при изменении маршрута
  useEffect(() => {
    setIsMenuOpen(false);
    setIsProjectDropdownOpen(false);
  }, [location.pathname]);

  const getPageTitle = () => {
    switch (location.pathname) {
      case '/':
        return 'Главная';
      case '/my-models':
        return 'Мои модели';
      case '/model-library':
        return 'Библиотека моделей';
      case '/datasets':
        return 'Датасеты';
      case '/fine-tuning':
        return 'Fine-Tuning';
      case '/usage':
        return 'Использование';
      case '/api-keys':
        return 'API Ключи';
      case '/admin/models':
        return 'Статус моделей';
      case '/admin/monitoring':
        return 'Мониторинг ETL';
      case '/sandbox':
        return 'Песочница';
      case '/projects':
        return 'RAG-проекты';
      case '/sources':
        return 'Источники';
      case '/storages':
        return 'Хранилища';
      case '/storage':
        return 'Хранилище';
      case '/integrations':
        return 'Интеграции';
      case '/tasks':
        return 'Задачи';
      default:
        if (location.pathname.startsWith('/projects/')) return 'RAG-проекты';
        if (location.pathname.startsWith('/sources/')) return 'Источники';
        if (location.pathname.startsWith('/storages/')) return 'Хранилища';
        if (location.pathname.startsWith('/storage/')) return 'Хранилище';
        if (location.pathname.startsWith('/integrations/')) return 'Интеграции';
        if (location.pathname.startsWith('/tasks/')) return 'Задачи';
        if (location.pathname.startsWith('/reporting/')) return 'Отчеты';
        if (location.pathname.startsWith('/feature-store/')) return 'Feature store';
        if (location.pathname.startsWith('/datasets/')) return 'Датасеты';
        if (location.pathname.startsWith('/chains')) return 'Цепочки';
        if (location.pathname.startsWith('/rec/campaigns')) return 'Кампании';
        if (location.pathname.startsWith('/rec/models')) return 'Модели';
        if (location.pathname.startsWith('/rec/placements')) return 'Места показа';
        if (location.pathname.startsWith('/cdp/')) return 'CDP';
        if (location.pathname.startsWith('/library')) return 'Библиотека';
        if (location.pathname.startsWith('/rag')) return 'RAG';
        return '';
    }
  };

  const handleProjectChange = (nextProjectDbName) => {
    if (nextProjectDbName) {
      setActiveProject(nextProjectDbName);
    } else {
      clearActiveProject();
    }

    const params = new URLSearchParams(location.search);
    const hadProjectInQuery = Boolean(params.get('db_name') || params.get('project_name'));

    if (nextProjectDbName) {
      params.set('db_name', nextProjectDbName);
      params.delete('project_name');
    } else {
      params.delete('db_name');
      params.delete('project_name');
    }

    const selected = projects.find(
      (project) => (project.db_name || project.name) === nextProjectDbName
    );

    if (selected && location.pathname.startsWith('/projects/') && projectIdFromPath) {
      const projectPathMatch = location.pathname.match(/^\/projects\/([^/]+)(.*)$/);
      const projectPathSuffix = projectPathMatch ? projectPathMatch[2] : '';
      const targetProjectPath = `/projects/${selected.project_id}${projectPathSuffix}`;
      const nextSearch = params.toString();
      setIsProjectDropdownOpen(false);
      navigate(`${targetProjectPath}${nextSearch ? `?${nextSearch}` : ''}`);
      return;
    }

    setIsProjectDropdownOpen(false);

    // Обновляем URL на project-scoped страницах и там, где проект уже в query
    // (иначе ActiveProjectContext откатит выбор обратно из URL).
    if (isScopedRoute || hadProjectInQuery) {
      const nextSearch = params.toString();
      navigate({ pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : '' });
    }
  };

  const handleLogout = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/logout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('email');
        localStorage.removeItem('user_id');
        logout();
        navigate('/auth');
      } else {
        console.error('Ошибка при выходе:', response.statusText);
      }
    } catch (error) {
      console.error('Ошибка при выходе:', error);
    }
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <div className={`header ${isAuthenticated ? 'authenticated' : ''}`}>
      <div className="site_page_header">
        <div className="header_item_1">
            {isAuthenticated && (
              <button
                type="button"
                className={`header-nav-toggle ${sidebarOpen ? 'active' : ''}`.trim()}
                onClick={onToggleSidebar}
                aria-label={sidebarOpen ? 'Закрыть меню' : 'Открыть меню'}
                aria-expanded={sidebarOpen}
              >
                <span className="burger-line"></span>
                <span className="burger-line"></span>
                <span className="burger-line"></span>
              </button>
            )}
            {isAuthenticated && (
              <div className="header_page_title">
                {getPageTitle()}
                <div className="header-project-context">
                  <label className="header-project-label" htmlFor="header-project-select">
                    Проект:
                  </label>
                  <div className="header-project-select-wrap" ref={projectDropdownRef}>
                    <button
                      id="header-project-select"
                      type="button"
                      className="header-project-select"
                      onClick={() => !projectsLoading && setIsProjectDropdownOpen((prev) => !prev)}
                      disabled={projectsLoading}
                      aria-haspopup="listbox"
                      aria-expanded={isProjectDropdownOpen}
                    >
                      {selectedProjectValue && selectedProjectStatus ? (
                        <span
                          className={projectStatusDotClass(selectedProjectStatus)}
                          aria-hidden="true"
                        />
                      ) : null}
                      <span className="header-project-selected-label">{selectedProjectLabel}</span>
                      <span className={`header-project-caret ${isProjectDropdownOpen ? 'open' : ''}`} aria-hidden="true">
                        ▾
                      </span>
                    </button>
                    {isProjectDropdownOpen && (
                      <div className="header-project-dropdown" role="listbox">
                        <button
                          type="button"
                          className={`header-project-option ${!selectedProjectValue ? 'active' : ''}`}
                          onClick={() => handleProjectChange('')}
                        >
                          <span className="header-project-option-name">Все проекты</span>
                        </button>
                        {projects.map((project) => (
                          <button
                            key={project.project_id}
                            type="button"
                            className={`header-project-option ${selectedProjectValue === (project.db_name || project.name) ? 'active' : ''}`}
                            onClick={() => handleProjectChange(project.db_name || project.name)}
                          >
                            <span
                              className={projectStatusDotClass(project.status)}
                              aria-hidden="true"
                            />
                            <span className="header-project-option-texts">
                              <span className="header-project-option-name">
                                {getProjectDisplayName(project)}
                              </span>
                              <span className="header-project-option-key">
                                {project.db_name || project.name}
                              </span>
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
        </div>
        <div className="header_item_2">
          {isAuthenticated ? (
            <div className="burger-menu" ref={menuRef}>
              <button 
                className={`burger-button ${isMenuOpen ? 'active' : ''}`}
                onClick={toggleMenu}
                aria-label="Открыть меню"
              >
                <span className="burger-line"></span>
                <span className="burger-line"></span>
                <span className="burger-line"></span>
              </button>
              
              {isMenuOpen && (
                <div className="dropdown-menu">
                  {email && (
                    <div className="dropdown-item account-item" title={email}>
                      {email}
                    </div>
                  )}
                  <Link 
                    to="/api-keys" 
                    className="dropdown-item"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    API ключи
                  </Link>
                  {isAdmin && (
                    <Link 
                      to="/admin/models" 
                      className="dropdown-item"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Адм.моделей
                    </Link>
                  )}
                  {isAdmin && (
                    <Link 
                      to="/admin/monitoring" 
                      className="dropdown-item"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Мониторинг ETL
                    </Link>
                  )}
                  <button 
                    className="dropdown-item logout-item"
                    onClick={handleLogout}
                  >
                    Выйти
                  </button>
                </div>
              )}
            </div>
          ) : (
            <a href="/auth" rel="noopener noreferrer">
              Войти
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

export default Header;
